/**
 * CATEGORY COLORS
 *
 * Centralized color definitions for item type categories.
 * Edit colors here to update them across the entire app.
 *
 * Each category has:
 * - bg: Base background color
 * - bgHover: Hover state background
 * - bgActive: Combined bg + hover for buttons
 * - text: Text color for labels
 * - border: Border color
 * - badge: Badge styling (bg + text)
 * - light: Light variant for subtle backgrounds
 * - ring: Focus ring color
 */

export const CategoryColors = {
  // Nail Polish - Indigo 500
  polish: {
    bg: 'bg-indigo-500',
    bgHover: 'hover:bg-indigo-600',
    bgActive: 'bg-indigo-500 hover:bg-indigo-600',
    bgMuted: 'bg-indigo-500/80',
    text: 'text-black',
    textHover: 'hover:text-black',
    border: 'border-indigo-500',
    badge: 'bg-indigo-500 text-white',
    badgeMuted: 'bg-indigo-500/80 text-white',
    light: 'bg-indigo-100/50',
    lightBorder: 'border-indigo-200/60',
    ring: 'ring-indigo-400',
  },

  // Stamping Plates - Pink 500
  plate: {
    bg: 'bg-pink-500',
    bgHover: 'hover:bg-pink-600',
    bgActive: 'bg-pink-500 hover:bg-pink-600',
    bgMuted: 'bg-pink-500/80',
    text: 'text-black',
    textHover: 'hover:text-black',
    border: 'border-pink-500',
    badge: 'bg-pink-500 text-white',
    badgeMuted: 'bg-pink-500/80 text-white',
    light: 'bg-pink-100/50',
    lightBorder: 'border-pink-200/60',
    ring: 'ring-pink-400',
  },

  // Transfers / Decals - Teal 500
  transfer: {
    bg: 'bg-teal-500',
    bgHover: 'hover:bg-teal-600',
    bgActive: 'bg-teal-500 hover:bg-teal-600',
    bgMuted: 'bg-teal-500/80',
    text: 'text-black',
    textHover: 'hover:text-black',
    border: 'border-teal-500',
    badge: 'bg-teal-500 text-white',
    badgeMuted: 'bg-teal-500/80 text-white',
    light: 'bg-teal-100/50',
    lightBorder: 'border-teal-200/60',
    ring: 'ring-teal-400',
  },

  // Embellishments - Violet 500
  embellishment: {
    bg: 'bg-violet-500',
    bgHover: 'hover:bg-violet-600',
    bgActive: 'bg-violet-500 hover:bg-violet-600',
    bgMuted: 'bg-violet-500/80',
    text: 'text-black',
    textHover: 'hover:text-black',
    border: 'border-violet-500',
    badge: 'bg-violet-500 text-white',
    badgeMuted: 'bg-violet-500/80 text-white',
    light: 'bg-violet-100/50',
    lightBorder: 'border-violet-200/60',
    ring: 'ring-violet-400',
  },

  // Tools - Amber 500
  tool: {
    bg: 'bg-amber-500',
    bgHover: 'hover:bg-amber-600',
    bgActive: 'bg-amber-500 hover:bg-amber-600',
    bgMuted: 'bg-amber-500/80',
    text: 'text-black',
    textHover: 'hover:text-black',
    border: 'border-amber-500',
    badge: 'bg-amber-500 text-white',
    badgeMuted: 'bg-amber-500/80 text-white',
    light: 'bg-amber-100/50',
    lightBorder: 'border-amber-200/60',
    ring: 'ring-amber-400',
  },

  // Buffing Powders - Cyan 500
  powder: {
    bg: 'bg-cyan-500',
    bgHover: 'hover:bg-cyan-600',
    bgActive: 'bg-cyan-500 hover:bg-cyan-600',
    bgMuted: 'bg-cyan-500/80',
    text: 'text-black',
    textHover: 'hover:text-black',
    border: 'border-cyan-500',
    badge: 'bg-cyan-500 text-white',
    badgeMuted: 'bg-cyan-500/80 text-white',
    light: 'bg-cyan-100/50',
    lightBorder: 'border-cyan-200/60',
    ring: 'ring-cyan-400',
  },

  // Acrylics - Rose 500
  acrylic: {
    bg: 'bg-rose-500',
    bgHover: 'hover:bg-rose-600',
    bgActive: 'bg-rose-500 hover:bg-rose-600',
    bgMuted: 'bg-rose-500/80',
    text: 'text-black',
    textHover: 'hover:text-black',
    border: 'border-rose-500',
    badge: 'bg-rose-500 text-white',
    badgeMuted: 'bg-rose-500/80 text-white',
    light: 'bg-rose-100/50',
    lightBorder: 'border-rose-200/60',
    ring: 'ring-rose-400',
  },

  // Cuticle Care - Emerald 500
  cuticle_care: {
    bg: 'bg-emerald-500',
    bgHover: 'hover:bg-emerald-600',
    bgActive: 'bg-emerald-500 hover:bg-emerald-600',
    bgMuted: 'bg-emerald-500/80',
    text: 'text-black',
    textHover: 'hover:text-black',
    border: 'border-emerald-500',
    badge: 'bg-emerald-500 text-white',
    badgeMuted: 'bg-emerald-500/80 text-white',
    light: 'bg-emerald-100/50',
    lightBorder: 'border-emerald-200/60',
    ring: 'ring-emerald-400',
  },
} as const;

// Type for category keys
export type CategoryKey = keyof typeof CategoryColors;

/**
 * PRIORITY COLORS (ISO/DISO/Unicorn)
 *
 * Centralized color definitions for wishlist priority levels.
 * Edit colors here to update them across the entire app.
 */
export const PriorityColors = {
  // ISO (In Search Of) - Pink (contrasts with blue diamond emoji)
  iso: {
    bg: 'bg-pink-500',
    bgHover: 'hover:bg-pink-600',
    text: 'text-pink-500',
    badge: 'bg-pink-500 text-white',
    label: '💎 ISO',
  },

  // DISO (Desperately In Search Of) - Teal (contrasts with orange flame emoji)
  diso: {
    bg: 'bg-teal-600',
    bgHover: 'hover:bg-teal-700',
    text: 'text-teal-600',
    badge: 'bg-teal-600 text-white',
    label: '🔥 DISO',
  },

  // Unicorn (Rare/Grail items) - Fuchsia (bright, stands out)
  unicorn: {
    bg: 'bg-fuchsia-600',
    bgHover: 'hover:bg-fuchsia-700',
    text: 'text-fuchsia-600',
    badge: 'bg-fuchsia-600 text-white',
    label: '🦄 Unicorn',
  },
} as const;

// Type for priority keys
export type PriorityKey = keyof typeof PriorityColors;

/**
 * UI COLORS (Muted/Secondary Elements)
 *
 * Centralized colors for muted UI elements like empty states,
 * dividers, and secondary text. Edit here to update visibility
 * across the entire app.
 */
export const UIColors = {
  // === For LIGHT backgrounds (bg-white, bg-gray-50, etc.) ===
  light: {
    primary: 'text-gray-900',      // Main headings/text
    secondary: 'text-gray-600',    // Secondary text, descriptions
    muted: 'text-gray-500',        // Tertiary text, stats, timestamps
    icon: 'text-gray-500',         // Icons on light bg
  },

  // === For GRADIENT/RAINBOW backgrounds ===
  // IMPORTANT: All text on the rainbow gradient background MUST be black for readability
  gradient: {
    primary: 'text-black',         // Main headings/text on gradient
    secondary: 'text-black',       // Secondary text on gradient (still black for readability)
    muted: 'text-black',           // Even muted text should be black on gradient
    link: 'text-black',            // Links on gradient (use with font-bold or hover:underline)
    icon: 'text-black',            // Icons on gradient background
  },

  // === Toggle Icon Colors (filled state) ===
  toggle: {
    favorite: 'fill-pink-500',     // Heart fill when favorited
    wishlist: 'fill-amber-500',    // Star fill when on wishlist
    rareLE: 'fill-purple-500',     // Gem fill when rare/LE
  },

  // === Muted elements (work on most backgrounds) ===
  mutedIcon: 'text-gray-500',
  divider: 'text-gray-400',
  secondaryText: 'text-gray-600',
  placeholder: 'text-gray-400',
} as const;

/**
 * Helper function to get colors for an item type.
 * Falls back to polish colors if type is unknown.
 */
export function getCategoryColors(itemType: string) {
  const key = itemType as CategoryKey;
  return CategoryColors[key] || CategoryColors.polish;
}

/**
 * Helper to get badge class for an item type.
 * Useful for rendering type badges consistently.
 */
export function getCategoryBadge(itemType: string): string {
  return getCategoryColors(itemType).badge;
}

/**
 * Helper function to get colors for a priority level.
 * Falls back to ISO colors if priority is unknown.
 */
export function getPriorityColors(priority: string) {
  const key = priority as PriorityKey;
  return PriorityColors[key] || PriorityColors.iso;
}

/**
 * Helper to get badge class for a priority level.
 */
export function getPriorityBadge(priority: string): string {
  return getPriorityColors(priority).badge;
}

/**
 * Helper to get label (with emoji) for a priority level.
 */
export function getPriorityLabel(priority: string): string {
  return getPriorityColors(priority).label;
}

/**
 * APP STYLES
 *
 * Centralized styling for common UI components.
 * Edit here to update glassmorphic effects, cards, buttons, etc.
 * across the entire app.
 */
export const AppStyles = {
  // === Cards & Containers ===
  card: 'bg-white/90 backdrop-blur-sm rounded-xl shadow-lg',
  cardHover: 'bg-white/90 backdrop-blur-sm rounded-xl shadow-sm hover:shadow-md transition-shadow',
  cardSubtle: 'bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl',

  // === Glassmorphic Backgrounds ===
  glass: 'bg-white/90 backdrop-blur-sm',
  glassLight: 'bg-white/80 backdrop-blur-sm',
  glassMuted: 'bg-gray-100/80 backdrop-blur-sm',
  glassOverlay: 'bg-black/50',

  // === Modals ===
  modal: 'bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl',
  modalHeader: 'bg-gray-600',
  modalHeaderWishlist: 'bg-gradient-to-r from-amber-500 to-yellow-500',

  // === Buttons ===
  buttonPrimary: 'bg-gray-600 text-white hover:bg-gray-700',
  buttonSecondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
  buttonWishlist: 'bg-amber-500 text-white hover:bg-amber-600',
  buttonDanger: 'bg-red-500 text-white hover:bg-red-600',
  buttonGhost: 'text-gray-600 hover:bg-white/50',

  // === Form Elements ===
  input: 'border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent',
  select: 'border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500',

  // === Logo/Avatar Fallbacks ===
  avatarFallback: 'bg-gray-400',

  // === Info/Callout Boxes ===
  infoBox: 'bg-gray-100/80 backdrop-blur-sm rounded-lg',

  // === Rating Display ===
  ratingBox: 'bg-gray-100/80 backdrop-blur-sm rounded-lg',
} as const;
